/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { useState } from "react";

import { useTheme } from "styled-components";

import { useCurrentUser } from "src/app/useCurrentUser";
import { Block } from "src/classes/block";
import { Connect as ConnectButton } from "src/components/UI/Buttons/IconButtons/Connect";
import { Expand as ExpandButton } from "src/components/UI/Buttons/IconButtons/Expand";
import { Like as LikeButton } from "src/components/UI/Buttons/IconButtons/Like";
import { isTouchDevice } from "src/lib/utils/device";

interface Props {
  block: Block;
  hovering: boolean;
  isCurrentSession: boolean;
}
/**
 * The buttons on the top right of a block
 */
export function BlockTopbarButtons({ block, hovering, isCurrentSession }: Props) {
  const theme = useTheme();
  const loggedIn = !!useCurrentUser();
  const [connectMenuOpen, setConnectMenuOpen] = useState(false);
  const isLiked = block.observe((state) => state.isLiked);
  const connectionCount = block.observe((state) => state.connectionCount);
  const hasConnections = connectionCount > 0;

  // button state logic is a little complicated...
  const showAllButtons = (isLiked || isTouchDevice() || (hovering && !isCurrentSession)) && loggedIn;
  const showLike = (showAllButtons || isLiked) && loggedIn; // isLiked is duplicated but makes it more readable
  const showExpand = (showAllButtons || hasConnections) && loggedIn;
  const showConnect = (showAllButtons || connectMenuOpen) && loggedIn;

  const styles = { borderLeft: `1px solid ${theme.block.border}` };
  return (
    <div style={{ display: "flex" }} className="hide-on-print">
      {showLike && <LikeButton like={() => block.like()} isLiked={isLiked} styles={styles} />}
      {showConnect && (
        <>
          <ConnectButton
            block={block}
            plate={null}
            menuOpen={connectMenuOpen}
            setMenuOpen={setConnectMenuOpen}
            id="cy-block-connect-button"
            styles={styles}
          />
        </>
      )}
      {showExpand && (
        <ExpandButton
          expand={() => block.open()}
          numConnects={connectionCount}
          id="cy-block-drilled-button"
          styles={styles}
        />
      )}
    </div>
  );
}
